import { db, meta, rows } from '@/db/database'
import { getProducts } from '@/db/repositories/catalog'
import { getOpenShift } from '@/db/repositories/shifts'
import { pickBatches } from '@/db/repositories/stock'
import { currentCashier, requireDevice } from '@/lib/device'
import { isTempId, newTempId } from '@/lib/tempId'
import { activeOffers, applyOffers, PosOffer } from '@/services/OfferEngine'
import { calculateTotals, discountAmount, lineGross, lineNet, memberDiscount, priceLines } from '@/services/PricingService'
import { toCustomer as toCustomerModel } from '@/db/repositories/customers'
import { syncEngine } from '@/sync/SyncEngine'
import { Paginated, PosHttpParams, PosModel, PosSync } from '@/types'

type Row = PosSync.LocalRow

export const OFFLINE_CREDIT_ALLOWANCE_KEY = 'settings.offline_credit_allowance'
export const RETURN_WINDOW_DAYS_KEY = 'settings.return_window_days'
/** Days after a sale it can be returned when the store sets none (POS.md O9); the server holds orders longer. */
export const DEFAULT_RETURN_WINDOW_DAYS = 7

const returnWindowDays = async () => (await meta.get<number | null>(RETURN_WINDOW_DAYS_KEY)) ?? DEFAULT_RETURN_WINDOW_DAYS

const withinReturnWindow = (createdAt: unknown, days: number) =>
  Date.now() - new Date(String(createdAt)).getTime() <= days * 24 * 3600 * 1000

const round = (n: number) => Math.round(n * 100) / 100

/** Points a row at its parent by server id once known, else by the parent's temp id (the server resolves both). */
const ref = (parent: Row, idField: string, tempField: string) =>
  parent._id ? { [idField]: parent._id } : { [tempField]: parent.temp_id }

/**
 * `YYYY-MMDD-T<unique_no>-<counter>`, the counter per terminal and day (§16 Q9). Sales and stand-alone repayment slips
 * (`receipt_no`, POS.md §18.8) share it, so a scanned ref is never both.
 */
export const nextNumber = async (uniqueNo: number, now: Date) => {
  const day = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`
  const counterKey = `order_counter.${day}`
  // A wiped or reinstalled device resumes after the highest number the server already holds for this terminal
  // and day (Q9); the pull brings this terminal's recent orders.
  const prefix = `${day}-T${uniqueNo}-`
  const used = (await rows('orders').toArray())
    .map((o) => String(o.order_ref ?? ''))
    .filter((r) => r.startsWith(prefix))
    .reduce((max, r) => Math.max(max, Number(r.slice(prefix.length)) || 0), 0)
  const counter = Math.max((await meta.get<number>(counterKey)) ?? 0, used) + 1
  await meta.set(counterKey, counter)
  return `${day}-T${uniqueNo}-${String(counter).padStart(4, '0')}`
}

const customerRow = async (id?: string) => (id ? rows('customers').get(id) : undefined)

/** Credit not yet confirmed by the server for this customer: credit sales minus repayments made on this device. */
const pendingCredit = async (customer: Row) => {
  const match = (row: Row) =>
    row.synced === 0 && (row.customer_id === customer._id || row.customer_user_temp_id === customer.temp_id)
  const sales = (await rows('payment_logs').toArray()).filter((p) => match(p) && p.payment_method === 'credit')
  const repaid = (await rows('credit_repayments').toArray()).filter(
    (r) => r.synced === 0 && (r.customer_id === customer._id || r.customer_temp_id === customer.temp_id)
  )
  return sales.reduce((s, p) => s + Number(p.amount_received), 0) - repaid.reduce((s, r) => s + Number(r.amount), 0)
}

/**
 * A credit sale must fit the customer's remaining credit as of the last pull, and the credit this device may give
 * before it syncs again: the customer's own allowance, else the tenant default, else 0 (§15.4, O8): with nothing set,
 * no credit is given offline beyond what the server has already confirmed.
 */
export const checkCredit = async (customerId: string | undefined, amount: number) => {
  const customer = await customerRow(customerId)
  if (!customer) throw new Error('Credit needs a customer.')
  const pending = await pendingCredit(customer)
  const remaining = Number(customer.credit_limit ?? 0) - Number(customer.credit_used ?? 0) - pending
  if (amount > remaining + 0.001) throw new Error(`Credit left for ${customer.name}: ${round(Math.max(remaining, 0))}.`)
  const allowance =
    customer.offline_credit_allowance ?? (await meta.get<number | null>(OFFLINE_CREDIT_ALLOWANCE_KEY)) ?? 0
  if (pending + amount > Number(allowance) + 0.001) {
    throw new Error(`This device can give ${customer.name} ${round(Math.max(Number(allowance) - pending, 0))} more credit before it syncs.`)
  }
}

const toCustomer = (row?: Row): PosModel.Customer | undefined =>
  row && {
    _id: row.id,
    name: String(row.name),
    phone: String(row.phone),
    loyalty_points: Number(row.reward_points ?? 0),
    total_orders: 0,
  }

/** Writes a sale to the device: order, one item per batch, payments. Sync carries it to the server. */
export const createOrder = async (body: PosHttpParams.CreateOrder): Promise<PosModel.Order> => {
  const device = requireDevice()
  if (!(await getOpenShift())) throw new Error('Open a shift before selling.')
  const profile = currentCashier()
  const now = new Date()
  const createdAt = now.toISOString()

  const { items: catalog } = await getProducts()
  const productById = new Map(catalog.map((p) => [p._id, p]))
  const cartLines: PosModel.CartLine[] = body.lines.map((l) => {
    const product = productById.get(l.product_id)
    if (!product) throw new Error('A product in the cart is no longer sold.')
    return { product, quantity: l.quantity, discount: l.discount, note: l.note }
  })
  const customer = await customerRow(body.customer_id)
  const buyer = customer ? toCustomerModel(customer, await rows('membership_tiers').toArray()) : undefined
  // The customer's price book, then offers, found and applied here on the till (§7.1, O3); the server stores the result.
  const lines = applyOffers(priceLines(cartLines, buyer), activeOffers((await rows('offers').toArray()) as unknown as PosOffer[], now))
  const orderDiscount = body.discount ?? memberDiscount(buyer)
  const totals = calculateTotals(lines, orderDiscount, 0, body.adjustment ?? 0)
  const paid = round(body.payments.reduce((s, p) => s + p.amount, 0))
  if (paid + 0.001 < totals.total) throw new Error('The payments do not cover the total.')
  const change = round(paid - totals.total)

  const creditAmount = body.payments.filter((p) => p.method === 'credit').reduce((s, p) => s + p.amount, 0)
  if (creditAmount > 0) await checkCredit(body.customer_id, creditAmount)

  const orderTempId = newTempId(device.terminal_unique_no)
  const number = await nextNumber(device.terminal_unique_no, now)
  const customerRef = customer ? ref(customer, 'customer_id', 'customer_temp_id') : {}
  const order: Row = {
    id: orderTempId,
    temp_id: orderTempId,
    synced: 0,
    terminal_id: device.terminal_id,
    staff_id: profile?._id ?? device.staff_id,
    ...customerRef,
    customer_info: customer ? { name: customer.name, phone: customer.phone } : {},
    order_ref: number,
    quantity: totals.itemCount,
    subtotal: totals.subtotal,
    discount: round(totals.lineDiscounts + totals.orderDiscount),
    vat: totals.tax,
    adjustment: totals.adjustment,
    total: totals.total,
    payment_method: body.payments.length === 1 ? body.payments[0].method : 'split',
    payment_status: 'paid',
    status: 'completed',
    created_at: createdAt,
  }

  // The order discount is spread over lines by value (as the totals do), so each item's revenue is what was paid.
  const afterLines = lines.reduce((s, l) => s + lineNet(l), 0)
  const share = afterLines > 0 ? (afterLines - totals.orderDiscount) / afterLines : 0
  // The round-off comes off the items' revenue the same way, so P&L and returns see what was paid; VAT stays.
  const beforeAdjustment = afterLines - totals.orderDiscount
  const adjustShare = beforeAdjustment > 0 ? (beforeAdjustment - totals.adjustment) / beforeAdjustment : 1
  const items: Row[] = []
  const stockTaken: PosModel.StockTaken[] = []
  for (const line of lines) {
    const unitBeforeAdjustment = (lineNet(line) * share) / line.quantity
    const unitVat = line.product.taxable ? (unitBeforeAdjustment * Number(line.product.sale_vat ?? 0)) / 100 : 0
    const unitNet = unitBeforeAdjustment * adjustShare
    for (const pick of await pickBatches(line.product._id, line.quantity)) {
      stockTaken.push({ name: line.product.name, batch: pick.stock_purchase_transaction_id, expiry_date: pick.expiry_date, quantity: pick.quantity })
      const itemTempId = newTempId(device.terminal_unique_no)
      items.push({
        id: itemTempId,
        temp_id: itemTempId,
        synced: 0,
        order_temp_id: orderTempId,
        product_id: line.product.product_id,
        product_variation_id: line.product._id,
        // A product created on this device and not synced yet is known to the server by its temp id.
        product_temp_id: isTempId(line.product.product_id) ? line.product.product_id : undefined,
        product_variation_temp_id: isTempId(line.product._id) ? line.product._id : undefined,
        stock_purchase_transaction_id: pick.stock_purchase_transaction_id,
        product_info: { name: line.product.name, sku: line.product.sku, unit: line.product.unit },
        quantity: pick.quantity,
        unit_price: line.product.price,
        // Omitted when unknown (cost not visible to this staff member): the server then uses the variation's cost.
        unit_purchase_price: line.product.purchase_cost,
        price: round(unitNet * pick.quantity),
        net_price: round((unitNet + unitVat) * pick.quantity),
        discount: round(discountAmount(lineGross(line), line.discount) * (pick.quantity / line.quantity)),
        discount_type: line.discount?.type === 'percent' ? 'percentage' : 'cash',
        item_vat: round(unitVat * pick.quantity),
        note: line.note,
        offer_id: line.offer_id ?? null,
        created_at: createdAt,
      })
    }
  }

  const payments: Row[] = body.payments.map((p, i) => {
    const paymentTempId = newTempId(device.terminal_unique_no)
    const isLastCash = p.method === 'cash' && i === body.payments.map((x) => x.method).lastIndexOf('cash')
    return {
      id: paymentTempId,
      temp_id: paymentTempId,
      synced: 0,
      terminal_id: device.terminal_id,
      order_temp_id: orderTempId,
      ...(customer ? (customer._id ? { customer_id: customer._id } : { customer_user_temp_id: customer.temp_id }) : {}),
      trx_id: paymentTempId,
      type: 'payment',
      message: `Payment for ${number}`,
      payment_method: p.method,
      transaction_id: p.reference ?? null,
      amount_received: p.amount,
      // Change is handed back from the cash drawer.
      amount_returned: isLastCash ? change : 0,
      status: 'paid',
      created_at: createdAt,
    }
  })

  await db.transaction('rw', [rows('orders'), rows('order_items'), rows('payment_logs')], async () => {
    await rows('orders').add(order)
    await rows('order_items').bulkAdd(items)
    await rows('payment_logs').bulkAdd(payments)
  })
  syncEngine.requestSync()

  return {
    _id: orderTempId,
    sync_state: 'pending',
    number,
    created_at: createdAt,
    cashier: profile?.name ?? '',
    customer: toCustomer(customer),
    lines,
    discount: orderDiscount,
    totals,
    payments: body.payments,
    change,
    status: 'completed',
    stock_taken: stockTaken,
    gross_profit: items.every((i) => i.unit_purchase_price != null)
      ? round(items.reduce((s, i) => s + Number(i.price) - Number(i.unit_purchase_price) * Number(i.quantity), 0))
      : undefined,
  }
}

const syncState = (row: Row): PosModel.Order['sync_state'] =>
  row.synced === 1 ? 'synced' : row.synced === -1 ? 'rejected' : 'pending'

/** Recent orders on this device, newest first, built back into the till's order shape. */
/** `shift`: only this terminal's sales in its open shift (the register's Orders tab); none when no shift is open. */
export const getOrders = async (query: { search?: string; shift?: boolean } = {}): Promise<Paginated<PosModel.Order>> => {
  const shift = query.shift ? await getOpenShift() : undefined
  if (query.shift && !shift) return { items: [], total: 0 }
  const terminalId = query.shift ? requireDevice().terminal_id : undefined
  const [all, items, payments, returnItems, customers, users] = await Promise.all([
    rows('orders').orderBy('created_at').reverse().limit(200).toArray(),
    rows('order_items').toArray(),
    rows('payment_logs').toArray(),
    rows('order_return_items').toArray(),
    rows('customers').toArray(),
    rows('users').toArray(),
  ])
  const orders = shift
    ? all.filter((o) => o.terminal_id === terminalId && String(o.created_at) >= String(shift.opened_at))
    : all
  const windowDays = await returnWindowDays()
  const search = query.search?.trim().toLowerCase()
  const belongs = (row: Row, order: Row) =>
    (row.order_temp_id && row.order_temp_id === order.temp_id) || (row.order_id && row.order_id === order._id)
  const returnedOf = (item: Row) =>
    returnItems
      .filter((r) => r.order_item_local_id === item.id || (item._id && r.order_item_id === item._id))
      .reduce((s, r) => s + Number(r.quantity), 0)

  const list: PosModel.Order[] = []
  for (const order of orders) {
    const customer = customers.find(
      (c) => (order.customer_id && c._id === order.customer_id) || (order.customer_temp_id && c.temp_id === order.customer_temp_id)
    )
    const customerName = customer?.name ?? (order.customer_info as any)?.name
    if (search && !`${order.order_ref} ${customerName ?? ''}`.toLowerCase().includes(search)) continue
    const orderItems = items.filter((i) => belongs(i, order))
    const sold = orderItems.reduce((s, i) => s + Number(i.quantity), 0)
    const returned = orderItems.reduce((s, i) => s + returnedOf(i), 0)
    const lines: PosModel.CartLine[] = orderItems.map((i) => {
      const info = (i.product_info ?? {}) as Record<string, any>
      return {
        quantity: Number(i.quantity),
        product: {
          _id: String(i.product_variation_id),
          product_id: String(i.product_id),
          name: String(info.name ?? ''),
          sku: String(info.sku ?? ''),
          barcodes: [],
          category_id: '',
          price: Number(i.unit_price),
          stock: 0,
          low_stock_threshold: 0,
          unit: String(info.unit ?? 'pc'),
          taxable: Number(i.item_vat) > 0,
        },
      }
    })
    const orderPayments = payments.filter((p) => belongs(p, order) && p.type === 'payment')
    list.push({
      _id: order.id,
      sync_state: syncState(order),
      number: String(order.order_ref),
      created_at: String(order.created_at),
      cashier: String(users.find((u) => u._id === order.staff_id)?.name ?? ''),
      customer: customer ? toCustomer(customer) : undefined,
      lines,
      totals: {
        subtotal: Number(order.subtotal),
        lineDiscounts: 0,
        orderDiscount: Number(order.discount),
        tax: Number(order.vat),
        adjustment: Number(order.adjustment ?? 0),
        total: Number(order.total),
        itemCount: Number(order.quantity),
      },
      payments: orderPayments.map((p) => ({
        method: p.payment_method as PosModel.PaymentMethod,
        amount: Number(p.amount_received),
        reference: (p.transaction_id as string) || undefined,
      })),
      change: orderPayments.reduce((s, p) => s + Number(p.amount_returned ?? 0), 0),
      status:
        order.status === 'cancelled' ? 'cancelled' : sold > 0 && returned >= sold ? 'refunded' : returned > 0 ? 'partially_returned' : 'completed',
      return_open: order.status !== 'cancelled' && withinReturnWindow(order.created_at, windowDays),
      cancellable: order.status !== 'cancelled' && returned === 0,
      returnable: orderItems.map((i) => ({
        item_id: i.id,
        name: String(((i.product_info ?? {}) as Record<string, any>).name ?? ''),
        quantity: Number(i.quantity),
        remaining: Number(i.quantity) - returnedOf(i),
        unit_paid: round(Number(i.net_price) / Number(i.quantity)),
      })),
    })
  }
  return { items: list, total: list.length }
}

export interface ReturnRequest {
  /** Order item `id`s (local) and how many of each come back. */
  lines: { item_id: string; quantity: number }[]
  /** Kept back from the refund (the reference's `return_charge`). */
  return_charge?: number
  refund_method: PosModel.PaymentMethod
  notes?: string
}

/**
 * Returns some or all of a sale, as the reference does: each line up to what has not come back yet, refunded at what
 * was paid for it (discounts and VAT included), less the return charge. Stock goes back to the batch each item came
 * from, and the server counts `returned_quantity` and the order status, once the return syncs (§15.9 rule 5).
 */
export const returnItems = async (orderId: string, request: ReturnRequest): Promise<PosModel.Order> => {
  const device = requireDevice()
  const order = await rows('orders').get(orderId)
  if (!order) throw new Error('Order not found.')
  const windowDays = await returnWindowDays()
  if (!withinReturnWindow(order.created_at, windowDays)) {
    throw new Error(`Returns are accepted for ${windowDays} days after the sale; this one is older.`)
  }
  const createdAt = new Date().toISOString()
  const [items, returnItemRows] = await Promise.all([rows('order_items').toArray(), rows('order_return_items').toArray()])
  const returnedOf = (item: Row) =>
    returnItemRows
      .filter((r) => r.order_item_local_id === item.id || (item._id && r.order_item_id === item._id))
      .reduce((sum, r) => sum + Number(r.quantity), 0)

  const returnTempId = newTempId(device.terminal_unique_no)
  const returnRows: Row[] = []
  let total = 0
  for (const line of request.lines.filter((l) => l.quantity > 0)) {
    const item = items.find((i) => i.id === line.item_id)
    if (!item) throw new Error('An item is not part of this order.')
    const left = Number(item.quantity) - returnedOf(item)
    if (line.quantity > left) throw new Error(`Only ${left} of ${(item.product_info as any)?.name ?? 'an item'} can come back.`)
    const unitPaid = Number(item.net_price) / Number(item.quantity)
    total += unitPaid * line.quantity
    const tempId = newTempId(device.terminal_unique_no)
    returnRows.push({
      id: tempId,
      temp_id: tempId,
      synced: 0,
      order_return_temp_id: returnTempId,
      ...ref(item, 'order_item_id', 'order_item_temp_id'),
      // Local link for the stock rebase, whatever ids the item has by then.
      order_item_local_id: item.id,
      quantity: line.quantity,
      unit_price: round(unitPaid),
      // Revenue reversed in the P&L: what the item earned, without VAT.
      subtotal: round((Number(item.price) / Number(item.quantity)) * line.quantity),
      created_at: createdAt,
    })
  }
  if (returnRows.length === 0) throw new Error('Choose at least one item to return.')
  total = round(total)
  const charge = round(Math.min(Math.max(request.return_charge ?? 0, 0), total))
  const refund = round(total - charge)

  const sameDay = await rows('order_returns').where('order_temp_id').equals(String(order.temp_id)).count()
  const refundTempId = newTempId(device.terminal_unique_no)
  await db.transaction('rw', [rows('order_returns'), rows('order_return_items'), rows('payment_logs')], async () => {
    await rows('order_returns').add({
      id: returnTempId,
      temp_id: returnTempId,
      synced: 0,
      ...ref(order, 'order_id', 'order_temp_id'),
      return_ref: `${order.order_ref}-R${sameDay + 1}`,
      total_refund_amount: total,
      return_charge: charge,
      notes: request.notes || null,
      created_at: createdAt,
    })
    await rows('order_return_items').bulkAdd(returnRows)
    if (refund > 0) {
      await rows('payment_logs').add({
        id: refundTempId,
        temp_id: refundTempId,
        synced: 0,
        terminal_id: device.terminal_id,
        ...ref(order, 'order_id', 'order_temp_id'),
        ...(order.customer_id ? { customer_id: order.customer_id } : {}),
        ...(order.customer_temp_id ? { customer_user_temp_id: order.customer_temp_id } : {}),
        trx_id: refundTempId,
        type: 'refund',
        message: `Refund for ${order.order_ref}`,
        payment_method: request.refund_method,
        amount_received: 0,
        amount_returned: refund,
        status: 'paid',
        created_at: createdAt,
      })
    }
  })
  syncEngine.requestSync()

  const { items: list } = await getOrders()
  const updated = list.find((o) => o._id === orderId)!
  return { ...updated, last_return: { ref: `${order.order_ref}-R${sameDay + 1}`, refund, charge } }
}

/** What a scanned ref is (POS.md §18.8): a sale, a return slip (`<order_ref>-R<n>`) or a repayment slip. */
export const findRef = async (
  code: string
): Promise<{ kind: 'order' | 'return'; order_ref: string } | { kind: 'repayment'; amount: number; customer?: string; created_at?: string } | null> => {
  const ret = code.match(/^(.+)-R\d+$/)
  if (ret && (await rows('order_returns').filter((r) => r.return_ref === code).first())) return { kind: 'return', order_ref: ret[1] }
  if (await rows('orders').filter((o) => o.order_ref === code).first()) return { kind: 'order', order_ref: code }
  const repayment = await rows('credit_repayments').filter((r) => r.receipt_no === code).first()
  if (repayment) {
    const customer = await customerRow(String(repayment.customer_id ?? repayment.customer_temp_id ?? ''))
    return { kind: 'repayment', amount: Number(repayment.amount ?? 0), customer: customer ? String(customer.name) : undefined, created_at: repayment.created_at as string }
  }
  return null
}

/**
 * Cancels a sale on the device (POS.md §15.2): the order is pushed again as `cancelled` and every payment is refunded
 * by its own method (credit goes back on the customer's account). The server restocks and reverses the P&L once, when
 * the cancel syncs; until then the stock shown adds the items back (`pendingMoves`). A sale with returns is not
 * cancelled; return the rest instead.
 */
export const cancelOrder = async (orderId: string, reason?: string): Promise<PosModel.Order> => {
  const device = requireDevice()
  const order = await rows('orders').get(orderId)
  if (!order) throw new Error('Order not found.')
  if (order.status === 'cancelled') throw new Error('This sale is already cancelled.')
  const belongs = (row: Row) => (row.order_temp_id && row.order_temp_id === order.temp_id) || (order._id && row.order_id === order._id)
  const items = (await rows('order_items').toArray()).filter(belongs)
  const returned = (await rows('order_return_items').toArray()).some((r) =>
    items.some((i) => r.order_item_local_id === i.id || (i._id && r.order_item_id === i._id))
  )
  if (returned) throw new Error('Items of this sale were returned; return the rest instead of cancelling.')

  const cancelledAt = new Date().toISOString()
  const payments = (await rows('payment_logs').toArray()).filter((p) => belongs(p) && p.type === 'payment')
  const refunds: Row[] = payments
    .map((p) => ({ p, net: round(Number(p.amount_received) - Number(p.amount_returned ?? 0)) }))
    .filter(({ net }) => net > 0)
    .map(({ p, net }) => {
      const tempId = newTempId(device.terminal_unique_no)
      return {
        id: tempId,
        temp_id: tempId,
        synced: 0,
        terminal_id: device.terminal_id,
        ...ref(order, 'order_id', 'order_temp_id'),
        ...(p.customer_id ? { customer_id: p.customer_id } : {}),
        ...(p.customer_user_temp_id ? { customer_user_temp_id: p.customer_user_temp_id } : {}),
        trx_id: tempId,
        type: 'refund',
        message: `Cancelled ${order.order_ref}${reason ? `: ${reason}` : ''}`,
        payment_method: p.payment_method,
        amount_received: 0,
        amount_returned: net,
        status: 'paid',
        created_at: cancelledAt,
      }
    })
  await db.transaction('rw', [rows('orders'), rows('payment_logs')], async () => {
    await rows('orders').update(orderId, { status: 'cancelled', cancelled_at: cancelledAt, cancel_reason: reason || null, synced: 0 })
    if (refunds.length) await rows('payment_logs').bulkAdd(refunds)
  })
  syncEngine.requestSync()
  const { items: list } = await getOrders()
  return list.find((o) => o._id === orderId)!
}

/** Returns everything not yet returned, refunded by the sale's own method (cash when it was split). */
export const refundOrder = async (orderId: string): Promise<PosModel.Order> => {
  const { items: list } = await getOrders()
  const order = list.find((o) => o._id === orderId)
  if (!order) throw new Error('Order not found.')
  const lines = (order.returnable ?? []).map((l) => ({ item_id: l.item_id, quantity: l.remaining }))
  const method = order.payments.length === 1 ? order.payments[0].method : 'cash'
  return returnItems(orderId, { lines, refund_method: method })
}
